import { TasksSource } from "@jugl-web/rest-api/tasks";
import { HookOutOfContextError } from "@jugl-web/utils";
import { FC, ReactNode, createContext, useContext, useMemo } from "react";
import { TaskGrouping, useTaskGrouping } from "../hooks/useTaskGrouping";
import { useTaskGroupingIdStorage } from "../storage/useTaskGroupingIdStorage";
import { useTaskListViewStorage } from "../storage/useTaskListViewStorage";

interface TaskGroupingContextValue {
  userSelectableGroupings: TaskGrouping[];
  selectedGrouping: TaskGrouping;
  selectGrouping: (groupingId: string) => void;
}

const TaskGroupingContext = createContext<TaskGroupingContextValue | null>(
  null
);

interface TaskGroupingProviderProps {
  children: ReactNode;
  entityId: string;
  source: TasksSource;
}

export const TaskGroupingProvider: FC<TaskGroupingProviderProps> = ({
  children,
  entityId,
  source,
}) => {
  const { userSelectableGroupings, getGroupingById } = useTaskGrouping({
    entityId,
    source,
  });

  const [taskListView] = useTaskListViewStorage({
    entityId,
    tasksSource: source,
  });

  const [taskGroupingId, setTaskGroupingId] = useTaskGroupingIdStorage({
    entityId,
    tasksSource: source,
    taskView: taskListView,
  });

  const selectedGrouping = useMemo(
    () => getGroupingById(taskGroupingId),
    [getGroupingById, taskGroupingId]
  );

  const contextValue: TaskGroupingContextValue = useMemo(
    () => ({
      userSelectableGroupings,
      selectedGrouping,
      selectGrouping: setTaskGroupingId,
    }),
    [userSelectableGroupings, selectedGrouping, setTaskGroupingId]
  );

  return (
    <TaskGroupingContext.Provider value={contextValue}>
      {children}
    </TaskGroupingContext.Provider>
  );
};

export const useTaskGroupingContext = () => {
  const context = useContext(TaskGroupingContext);

  if (!context) {
    throw new HookOutOfContextError(
      "useTaskGroupingContext",
      "TaskGroupingContext"
    );
  }

  return context;
};
